import React, { useState, useEffect, useMemo } from "react";
import {
  Box,
  Button,
  TextField,
  Typography,
  Alert,
  Autocomplete,
} from "@mui/material";
import { useNavigate } from "react-router-dom";
import { useDispatch, useSelector } from "react-redux";
import Layout from "./Layout";
import { superlogin } from "../actions/authenActions";

const Admin = () => {
  const dispatch = useDispatch();
  const navigate = useNavigate();

  const [formData, setFormData] = useState({
    user_id: "",
    password: "",
    org_email: "",
  });

  const [formErrors, setFormErrors] = useState({});
  const [submissionError, setSubmissionError] = useState(null);

  const loggedInUser = useSelector((state) => state.authen.loggedInUser);
  const loggedInAdmin = useSelector((state) => state.authen.loggedInAdmin);
  const accessToken = useSelector((state) => state.authen.accessToken);

  // Build organization options from loggedInAdmin
  const organizationOptions = useMemo(() => {
    if (!loggedInAdmin || !loggedInAdmin.data) return [];

    return [...loggedInAdmin.data]
      .sort((a, b) =>
        (a.organization_name || "").localeCompare(b.organization_name || ""),
      )
      .map((item) => ({
        label: item.organization_name,
        email: item.email,
      }));
  }, [loggedInAdmin]);

  // STEP 1: After first login, wait for org selection instead of navigating
  useEffect(() => {
    if (loggedInUser) {
      if (loggedInUser.status === "Pass") {
        // If we already have org options, wait for selection
        if (!organizationOptions.length) {
          navigate("/dashboard");
        }
      } else {
        setSubmissionError(loggedInUser.message || "Login failed");
      }
    }
  }, [loggedInUser, organizationOptions, navigate]);

  function validate() {
    const errors = {};

    if (!formData.user_id) {
      errors.user_id = "Username is required";
    }

    if (!formData.password) {
      errors.password = "Password is required";
    }

    if (organizationOptions.length > 0 && !formData.org_email) {
      errors.org_email = "Please select an organization";
    }

    return errors;
  }

  function handleChange(e) {
    const { name, value } = e.target;
    setFormData((prev) => ({
      ...prev,
      [name]: value,
    }));
    setFormErrors((prev) => ({
      ...prev,
      [name]: "",
    }));
    setSubmissionError(null);
  }

  function handleSubmit(e) {
    e.preventDefault();

    const errors = validate();
    setFormErrors(errors);

    if (Object.keys(errors).length > 0) {
      return;
    }

    // Conditionally include org_email as the 3rd parameter
    const payload = {
      user_id: formData.user_id,
      password: formData.password,
      ...(formData.org_email ? { email: formData.org_email } : {}),
      ...(accessToken ? { accessToken: accessToken } : {}),
    };

    dispatch(superlogin(payload))
      .then(() => {
        if (formData.org_email) {
          navigate("/dashboard");
        }
      })
      .catch((error) => {
        setSubmissionError(error?.message || "An unexpected error occurred");
      });
  }

  const shouldShowOrgSelect = organizationOptions.length > 0;

  return (
    <>
      <Layout>
        <Typography variant="h5" gutterBottom>
          Admin Login
        </Typography>

        <Box
          component="form"
          onSubmit={handleSubmit}
          noValidate
          autoComplete="off"
          sx={{ mt: 2 }}
        >
          {submissionError && (
            <Alert severity="error" sx={{ mb: 2 }}>
              {submissionError}
            </Alert>
          )}

          <TextField
            label="Username"
            name="user_id"
            fullWidth
            margin="normal"
            value={formData.user_id}
            onChange={handleChange}
            error={!!formErrors.user_id}
            helperText={formErrors.user_id}
          />

          <TextField
            label="Password"
            type="password"
            name="password"
            fullWidth
            margin="normal"
            value={formData.password}
            onChange={handleChange}
            error={!!formErrors.password}
            helperText={formErrors.password}
          />

          {shouldShowOrgSelect && (
            <Autocomplete
              options={organizationOptions}
              getOptionLabel={(option) => option.label || ""}
              filterOptions={(options, state) => {
                const input = (state.inputValue || "").toLowerCase();

                return options.filter((option) =>
                  (option.label || "").toLowerCase().includes(input),
                );
              }}
              value={
                organizationOptions.find(
                  (org) => org.email === formData.org_email,
                ) || null
              }
              onChange={(event, newValue) => {
                setFormData((prev) => ({
                  ...prev,
                  org_email: newValue ? newValue.email : "",
                }));
                setFormErrors((prev) => ({
                  ...prev,
                  org_email: "",
                }));
              }}
              renderInput={(params) => (
                <TextField
                  {...params}
                  label="Search Organization"
                  margin="normal"
                  error={!!formErrors.org_email}
                  helperText={formErrors.org_email}
                />
              )}
              fullWidth
            />
          )}
          <Button fullWidth variant="contained" type="submit" sx={{ mt: 2 }}>
            {shouldShowOrgSelect ? "Continue" : "Login"}
          </Button>
        </Box>
      </Layout>
    </>
  );
};

export default Admin;
